import React from "react";

import {
	Button,
	Card,
	Divider,
	Progress,
	Title,
} from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { cn } from "@/utils/cn";

import { TOnboardingStep } from "@/types";

import { ONBOARDING_MAX_WIDTH_CLASS, ONBOARDING_STEPS } from "../COnboarding";

export interface IOnboardingStepCardProps {
	step: TOnboardingStep;
	title: string;
	intro: string;
	children: React.ReactNode;
	onBack: () => void;
	/** Rendered as a link-style "Skip this step" button when given. */
	onSkip?: () => void;
	onNext: () => void;
	isSubmitting?: boolean;
	nextLabel?: string;
}

/**
 * The frame every form step shares: progress + "Step n of 3", heading,
 * intro, the step's own fields, then the Back / Skip / Continue row.
 * The step decides what "Continue" does (usually `formik.submitForm`).
 */
export const OnboardingStepCard: React.FC<IOnboardingStepCardProps> = ({
	step,
	title,
	intro,
	children,
	onBack,
	onSkip,
	onNext,
	isSubmitting = false,
	nextLabel,
}) => {
	const index = ONBOARDING_STEPS.indexOf(step);
	const total = ONBOARDING_STEPS.length;
	const progress = Math.round((index / total) * 100);

	return (
		<Card
			elevation={2}
			className={cn([
				"flex w-full flex-col gap-6",
				ONBOARDING_MAX_WIDTH_CLASS,
			])}
		>
			<div className="flex flex-col gap-2">
				<Progress
					value={progress}
					size="sm"
					showValue
					label={t("onboarding.progress")}
				/>
				<div className="text-xs text-[var(--amp-color-sub)]">
					{t("onboarding.stepOf", { index: index + 1, total })} ·{" "}
					{t(`onboarding.steps.${step}.name`)}
				</div>
			</div>
			<div className="flex flex-col gap-2">
				<Title as="h1" size="h3">
					{title}
				</Title>
				<p className="m-0 text-sm leading-[22px] text-[var(--amp-color-sub)]">
					{intro}
				</p>
			</div>
			{children}
			<Divider />
			<div className="flex flex-wrap items-center justify-between gap-4">
				<Button
					variant="ghost"
					onClick={onBack}
					disabled={isSubmitting}
				>
					{t("actions.back")}
				</Button>
				<div className="flex items-center gap-4">
					{onSkip && (
						<Button
							variant="link"
							color="neutral"
							onClick={onSkip}
							disabled={isSubmitting}
						>
							{t("onboarding.skip")}
						</Button>
					)}
					<Button onClick={onNext} isLoading={isSubmitting}>
						{nextLabel ?? t("actions.continue")}
					</Button>
				</div>
			</div>
		</Card>
	);
};
